<template lang="pug">
    .draggable(
        @mousedown="handleMouseDown"
        @touchstart="handleMouseDown"
    )
        slot
</template>

<script>
    export default {
        props: {
            startPosition: {
                type: Object,
                default: () => ({
                    bottom: '20px',
                    right: '20px',
                }),
            },
        },
        data() {
            return {
                draggable: null,
                isDragging: false,
                timerId: null,
                isPageScrollable: false,
                pageYOffset: null,
                notVisibleContentHeight: null,
                shiftInfo: null,
                scrollBarWidth: window.innerWidth - document.documentElement.clientWidth,
            };
        },
        mounted() {
            this.draggable = document.querySelector('.draggable');
            this.draggable.style.top = this.startPosition.top ?? 'auto';
            this.draggable.style.right = this.startPosition.right ?? 'auto';
            this.draggable.style.bottom = this.startPosition.bottom ?? 'auto';
            this.draggable.style.left = this.startPosition.left ?? 'auto';

            this.isPageScrollable = document.documentElement.scrollHeight > document.documentElement.clientHeight;

            window.addEventListener('mouseup', this.stopDrag);
            window.addEventListener('touchend', this.stopDrag);
        },
        methods: {
            stopDrag() {
                if (this.isPageScrollable) {
                    this.makeBodyScrollable();
                }
                if (this.isPageScrollable) {
                    this.draggable.style.bottom =
                        parseInt(this.draggable.style.bottom) -
                        window.pageYOffset -
                        this.notVisibleContentHeight +
                        'px';
                }
                clearTimeout(this.timerId);
                if (this.draggable) {
                    document.removeEventListener('mousemove', this.doDrag);
                    document.removeEventListener('touchmove', this.doDrag);
                    window.removeEventListener('mouseup', this.stopDrag);
                    window.removeEventListener('touchend', this.stopDrag);

                    this.dragging = false;
                    document.body.style.userSelect = 'auto';
                }
            },
            handleMouseDown(event) {
                const pageX = event.type === 'touchstart' ? event.touches?.[0].screenX : event.pageX;
                const pageY = event.type === 'touchstart' ? event.touches?.[0].screenY - this.pageYOffset : event.pageY;
                if (this.isPageScrollable) {
                    this.notVisibleContentHeight =
                        document.documentElement.scrollHeight - document.documentElement.clientHeight;
                    this.draggable.style.bottom =
                        parseInt(this.draggable.style.bottom) + this.notVisibleContentHeight + 'px';
                }

                if (this.isPageScrollable) {
                    this.makeBodyNotScrollable();
                }
                const coords = this.getCoords(this.draggable);

                const shiftXLeft = pageX - coords.left;
                const shiftYTop = pageY - coords.top;
                this.shiftInfo = {
                    shiftXLeft: shiftXLeft,
                    shiftYTop: shiftYTop,
                    shiftXRight: coords.width - shiftXLeft,
                    shiftYBottom: coords.height - shiftYTop,
                };

                this.timerId = setTimeout(() => (this.dragging = true), 100);

                document.addEventListener('mousemove', this.doDrag);
                document.addEventListener('touchmove', this.doDrag);
                window.addEventListener('mouseup', this.stopDrag);
                window.addEventListener('touchend', this.stopDrag);

                document.body.style.userSelect = 'none';
            },
            doDrag(event) {
                const pageX = event.type === 'touchmove' ? event.changedTouches?.[0].screenX : event.pageX;
                const pageY =
                    event.type === 'touchmove' ? event.changedTouches?.[0].screenY - this.pageYOffset : event.pageY;

                if (this.dragging) {
                    const { shiftXLeft, shiftYTop, shiftXRight, shiftYBottom } = this.shiftInfo;

                    this.draggable.style.left = 'auto';
                    this.draggable.style.top = 'auto';
                    this.draggable.style.right = window.innerWidth - pageX - shiftXRight - this.scrollBarWidth + 'px';

                    this.draggable.style.bottom =
                        window.innerHeight +
                        this.notVisibleContentHeight -
                        this.pageYOffset -
                        pageY -
                        shiftYBottom +
                        'px';

                    if (window.innerWidth - pageX - shiftXRight - 10 < 0) {
                        this.draggable.style.right = 10 + 'px';
                    } else if (window.innerWidth - pageX + shiftXLeft + 20 > window.innerWidth) {
                        this.draggable.style.right = 'auto';
                        this.draggable.style.left = 10 + 'px';
                    }

                    if (pageY + shiftYBottom + 10 > window.innerHeight) {
                        this.draggable.style.top = 'auto';
                        this.draggable.style.bottom = this.notVisibleContentHeight - this.pageYOffset + 10 + 'px';
                    } else if (pageY - shiftYTop - 10 < 0) {
                        this.draggable.style.bottom = 'auto';
                        this.draggable.style.top = 10 + this.pageYOffset + 'px';
                    }
                }
            },
            getCoords(elem) {
                const box = elem.getBoundingClientRect();
                return {
                    top: box.top + this.pageYOffset,
                    left: box.left + window.pageXOffset,
                    width: box.width,
                    height: box.height,
                };
            },
            makeBodyNotScrollable() {
                if (window.pageYOffset) {
                    this.pageYOffset = window.pageYOffset;
                    document.body.style.top = -this.pageYOffset + 'px';
                }
                document.body.classList.add('body_no-scroll');
            },
            makeBodyScrollable() {
                document.body.style.top = '0px';
                this.$nextTick(() => {
                    window.scroll(0, this.pageYOffset);
                    this.pageYOffset = null;
                });

                document.body.classList.remove('body_no-scroll');
            },
        },
    };
</script>

<style lang="scss" scoped>
    .draggable {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        position: absolute;
        z-index: 11;
        background-color: $x-color-white;
        border-radius: $border-radius-40;
        box-shadow: 0 0 40px rgba(51, 51, 51, 0.4);
        user-select: none;
    }
</style>

<style lang="scss">
    .body_no-scroll {
        position: fixed;
        overflow-y: scroll;
        width: 100%;
    }
</style>
